import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import no_img from '../../../assets/images/picture.png';
import { Badge } from '../../../components/ui/badge';
import { Button as ShadcnButton } from '../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { CircleDot, Edit, Eye, EyeOff } from 'lucide-react';
import { Button, LoadingTable } from '../../../components/base';
import { DataTable, IDataTableColumn } from '../../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../../components/container';
import { useLookupArticleCategory } from '../../../components/shared/hoc-article-category/useLookupArticleCategory';
import { ArticleActionConfirmRequest } from '../../../context/models/articles/ArticleActionConfirmRequest';
import { ArticleItemResponse, ArticleModel } from '../../../context/models/articles/ArticleModel';
import { ArticleSelectRequest, df_ArticleSelectRequest } from '../../../context/models/articles/ArticleSelectRequest';
import { eArticleStatus } from '../../../context/models/articles/eArticleStatus';
import { BasePaginationRespone } from '../../../context/base/BasePaginationRespone';
import { BaseShowDetailId } from '../../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { PaginationRender } from '../../../utils/PaginationHelper';
import { createUUID } from '../../../utils/createUUID';
import { ArticleHeaderButton } from '../button-actions/ArticleHeaderButton';
import { ChangeStatusConfirm } from '../change-status-confirm';
import FilterBox from '../filter-box';
import { history } from '../../../utils/history';
import { getArticleStatusName } from '../../../context/models/articles/eArticleStatus';

type IArticleTablePageProps = {
	titlePage: string;
	articleStatus: eArticleStatus;
	langCode: string;
	isOwner: boolean;
	status: eBaseActionStatus;
	paginationResponse: BasePaginationRespone<ArticleItemResponse>;
	selectedIds: string[];
	showDetail: BaseShowDetailId<string>;
	showConfirm: ArticleActionConfirmRequest;
	handleReloadData: (request: ArticleSelectRequest) => void;
	handleGetDetail: (id: string) => void;
	handleSaveData: (data: ArticleModel) => void;
	handleDeleteData: (data: string[]) => void;
	onChangeSelectedIds: (ids: string[]) => void;
	handleChangeStatus: (data: ArticleActionConfirmRequest) => void;
	onShowDetail: (data: BaseShowDetailId<string>) => void;
	onShowConfirm: (data: ArticleActionConfirmRequest) => void;
};

const ArticleTable = (props: IArticleTablePageProps) => {
	const navigate = useNavigate();
	const { article_category_all } = useLookupArticleCategory();
	const [showFilter, setShowFilter] = useState(false);
	const location = useLocation();
	const {
		titlePage,
		langCode,
		articleStatus,
		isOwner,
		status,
		selectedIds,
		showConfirm,
		paginationResponse,
		handleReloadData,
		handleChangeStatus,
		onChangeSelectedIds,
		onShowConfirm
	} = props;
	const { listDatas, pagination } = paginationResponse;
	const [filter, setFilter] = useState<ArticleSelectRequest>({ ...df_ArticleSelectRequest, status: articleStatus, langCode: langCode });

	const handleAddEditAction = (id?: string) => {
		history.replace(location.pathname);
		navigate(`${location.pathname}/edit/${id || '0'}`, { replace: false });
	};

	const handleReloadDataWithFilter = useCallback(() => {
		setShowFilter(false);
		handleReloadData(filter);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	useEffect(() => {
		handleReloadDataWithFilter();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadDataWithFilter();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<ArticleItemResponse>[] => {
		const statusTone: Record<string, string> = {
			PUBLISHED: 'border-emerald-300/70 bg-emerald-50 text-emerald-700',
			DRAFT: 'border-slate-300/70 bg-slate-100 text-slate-700',
			APPROVING: 'border-blue-300/70 bg-blue-50 text-blue-700',
			EDITING: 'border-indigo-300/70 bg-indigo-50 text-indigo-700',
			SENTAPPROVING: 'border-cyan-300/70 bg-cyan-50 text-cyan-700',
			SENTEDITING: 'border-violet-300/70 bg-violet-50 text-violet-700',
			REMOVED: 'border-amber-300/70 bg-amber-50 text-amber-700',
			DELETED: 'border-rose-300/70 bg-rose-50 text-rose-700',
			BACK: 'border-orange-300/70 bg-orange-50 text-orange-700',
			RETURN: 'border-fuchsia-300/70 bg-fuchsia-50 text-fuchsia-700',
			RESTORE: 'border-teal-300/70 bg-teal-50 text-teal-700',
		};

		return [
			{
				accessorKey: 'title',
				header: 'Tiêu đề',
				size: 300,
				cell: ({ row }) => {
					const article = row.original;
					return (
						<div className='flex items-start gap-4 transition-colors py-1'>
							<div className='hidden md:flex flex-shrink-0 w-16 h-16 rounded-md border border-border/50 bg-background overflow-hidden items-center justify-center shadow-sm'>
								<img src={article.image?.length > 0 ? article.image : no_img} alt='ảnh đại diện' className='object-cover w-full h-full rounded-sm' />
							</div>
							<div className='flex-1 space-y-1.5'>
								<h6 className='text-sm font-semibold text-foreground/90'>
									<em className='ni ni-article text-primary mr-1 flex-shrink-0'></em>
									{article.title}
								</h6>
								<p className='text-xs text-muted-foreground'>
									NXB: <span className='font-medium text-foreground/80'>{article.publishedOnDate}</span>, Độ dài: <span className='font-medium text-foreground/80'>{article.wordCount} từ</span>.
								</p>
								<p className='text-xs text-muted-foreground hidden md:block italic truncate max-w-[500px]'>
									Sapo: {article.initContent}
								</p>
								<p className='text-[11px] text-muted-foreground/70'>
									Tạo bởi: {article.createdBy} ({article.createdOnDate}); Sửa bởi: {article.lastModifiedBy} ({article.lastModifiedOnDate}).
								</p>
							</div>
						</div>
					);
				}
			},
			{ 
				accessorKey: 'catId', 
				header: 'Chuyên mục', 
				size: 150,
				cell: ({ getValue }) => {
					const val = getValue() as string;
					const group = article_category_all.find((g: any) => g.id === val);
					return <span className="font-medium text-primary/80">{group ? group.name : val}</span>;
				}
			},
			{
				id: 'status',
				accessorKey: 'status',
				header: 'Trạng thái',
				size: 150,
				cell: ({ row }) => {
					const statusValue = String(row.original.status || 'DRAFT');
					return (
						<Badge
							variant='outline'
							className={`inline-flex min-w-[116px] items-center justify-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone[statusValue] || 'border-muted bg-muted/30 text-foreground/80'}`}
						>
							<CircleDot className='h-3 w-3' />
							{getArticleStatusName(statusValue)}
						</Badge>
					);
				}
			},
			{
				id: 'visibility',
				header: 'Ẩn/Hiện',
				size: 90,
				cell: ({ row }) => {
					const isHidden = Boolean(
						(row.original as any).isHiddenContent ?? (row.original as any).isHidden ?? (row.original as any).hidden
					);
					return (
						<TooltipProvider delayDuration={150}>
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type='button'
										className={!isHidden
											? 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-emerald-300/70 bg-emerald-50 text-emerald-700'
											: 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-amber-300/70 bg-amber-50 text-amber-700'}
										aria-label={!isHidden ? 'Đang hiển thị' : 'Đang ẩn'}
									>
										{!isHidden ? <Eye className='h-3.5 w-3.5' /> : <EyeOff className='h-3.5 w-3.5' />}
									</button>
								</TooltipTrigger>
								<TooltipContent>
									{!isHidden ? 'Hiển thị công khai' : 'Đang ẩn khỏi giao diện'}
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					);
				}
			},
			{
				id: 'actions',
				header: 'Sửa',
				meta: { align: 'center', className: 'sticky right-0 z-20 bg-background/95' },
				size: 70,
				cell: ({ row }) => (
					<ShadcnButton
						variant='outline'
						size='icon'
						className='h-9 w-9 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary focus-visible:ring-2 focus-visible:ring-primary'
						onClick={(e) => {
							e.stopPropagation();
							handleAddEditAction(row.original.id);
						}}
						title='Sửa bài viết'
						aria-label='Sửa bài viết'
					>
						<Edit className='h-4 w-4' />
					</ShadcnButton>
				)
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [article_category_all]);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`} description='Quản lý vòng đời bài viết theo trạng thái biên tập, kiểm duyệt và xuất bản.'>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<ArticleHeaderButton
								isOwner={isOwner}
								articleStatus={articleStatus}
								selectedIds={selectedIds}
								onActionConfirm={(actionChange, selectedIds) =>
									onShowConfirm({ isShow: true, actionStatus: actionChange, ids: selectedIds })
								}
							/>
							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => handleAddEditAction()}
								/>
							</li>
							<li>
								<Button
									text='Lọc tin bài'
									icon='icon ni ni-search'
									theme='info'
									className='hidden md:inline-flex'
									onClick={() => setShowFilter(true)}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<div className='flex flex-wrap items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs'>
						<span className='font-medium text-foreground/80'>Chú thích trạng thái:</span>
						<span className='inline-flex items-center gap-1 text-emerald-700'><span className='h-2 w-2 rounded-full bg-emerald-500' />Xuất bản</span>
						<span className='inline-flex items-center gap-1 text-blue-700'><span className='h-2 w-2 rounded-full bg-blue-500' />Đang duyệt/biên tập</span>
						<span className='inline-flex items-center gap-1 text-amber-700'><span className='h-2 w-2 rounded-full bg-amber-500' />Gỡ ẩn</span>
						<span className='inline-flex items-center gap-1 text-slate-700'><span className='h-2 w-2 rounded-full bg-slate-500' />Nháp</span>
					</div>

					<div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
						<div className='rounded-xl border border-border/60 bg-card/80 px-4 py-3'>
							<p className='text-xs uppercase tracking-wider text-muted-foreground'>Tổng bài viết</p>
							<p className='mt-1 text-2xl font-bold'>{pagination?.totalRecords || 0}</p>
						</div>
						<div className='rounded-xl border border-border/60 bg-card/80 px-4 py-3'>
							<p className='text-xs uppercase tracking-wider text-muted-foreground'>Đang chọn</p>
							<p className='mt-1 text-2xl font-bold text-primary'>{selectedIds.length}</p>
						</div>
						<div className='rounded-xl border border-border/60 bg-card/80 px-4 py-3'>
							<p className='text-xs uppercase tracking-wider text-muted-foreground'>Trang hiện tại</p>
							<p className='mt-1 text-2xl font-bold'>{pagination?.currentPage || 1}/{pagination?.totalPages || 1}</p>
						</div>
					</div>

					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={listDatas}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								searchMode='remote'
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [id]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]));
								}}
								onRowClick={(row) => handleAddEditAction(row.id)}
								enablePagination
                                // @ts-ignore: Assuming pagination exists on paginationResponse
								totalRecords={pagination?.totalRecords || listDatas.length}
								enableGlobalFilter
								toolbar={{
									isShowSearch: true,
									searchPlaceholder: 'Tìm kiếm bài viết...',
									searchValue: filter.searchString,
									onSearchChange: (value) => setFilter((prev) => ({ ...prev, searchString: value }))
								}}
								enableExport
								exportTitle={titlePage}
								onRefresh={handleReloadDataWithFilter}
								height={window.innerHeight - 300}
								noDataText={filter.searchString ? 'Không tìm thấy bài viết phù hợp' : 'Không có bài viết'}
							/>
						)}
					</BlockCard>
				</PageBody>
				
			</PageContainer>
			{showConfirm && showConfirm.isShow && (
				<>
					<ChangeStatusConfirm
						isShow={showConfirm.isShow}
						onClose={() => onShowConfirm({ isShow: false, actionStatus: showConfirm.actionStatus, ids: [] })}
						onConfirmStatus={() => handleChangeStatus(showConfirm)}
						isLoading={status === eBaseActionStatus.loading}
						actionStatus={showConfirm.actionStatus}
						selectedIds={selectedIds}
					/>
				</>
			)}
			{showFilter && (
				<FilterBox
					filterModel={filter}
					isLoading={status === eBaseActionStatus.loading}
					isShow={showFilter}
					onClose={() => setShowFilter(false)}
					onSubmit={(filterChanged) => setFilter((prev) => ({ ...prev, ...filterChanged }))}
				/>
			)}
		</>
	);
};

export default ArticleTable;
